import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Alert, Box, Button, TextField, Typography } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useTranslation } from "react-i18next";

const HEADERS = [
  "sku","nome","preco","estoque","preco_comdesconto","status","tipo",
  "id_categoria","id_subcategoria","peso","largura","altura","comprimento",
  "detalhes","imagens_urls",
];

const HEADER_LINE = "sku;nome;preco;estoque;preco_comdesconto;status;tipo;id_categoria;id_subcategoria;peso;largura;altura;comprimento;descricao;imagens";

const PasteDialog = ({
  open,
  text,
  setText,
  preview,
  onPreview,
  onApply,
  applying,
  onClose,
}) => {
  const { t } = useTranslation("products");

  const fillExample = () => {
    setText(t("pasteDialog.exampleCsv"));
    setTimeout(onPreview, 0);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t("pasteDialog.title")}</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" component="div">
            <strong>{t("pasteDialog.acceptedFormats")}</strong>
            <br />
            <br />
            {t("pasteDialog.formatWithHeader")}
            <br />
            <code>{HEADER_LINE}</code>
            <br />
            <br />
            {t("pasteDialog.formatPairs")}
            <br />
            <strong>{t("pasteDialog.requiredLabel")}</strong>{" "}
            <code>nome</code>, <code>preco</code>, <code>estoque</code>.
          </Typography>
        </Alert>

        <TextField
          multiline minRows={8} placeholder={t("pasteDialog.placeholder")}
          fullWidth value={text} onChange={(e) => setText(e.target.value)} onBlur={onPreview}
        />

        <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button variant="outlined" onClick={onPreview}>{t("pasteDialog.preview")}</Button>
          <Button onClick={fillExample}>
            {t("pasteDialog.fillExample")}
          </Button>
        </Box>

        {(preview.rowsValid.length > 0 || preview.rowsInvalid.length > 0) && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t("pasteDialog.previewSummary", { valid: preview.rowsValid.length })}
              {!!preview.rowsInvalid.length && t("pasteDialog.previewInvalid", { invalid: preview.rowsInvalid.length })}
            </Typography>

            {preview.rowsValid.length > 0 && (
              <Box sx={{ maxHeight: 300, overflow: "auto", mb: 2 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {HEADERS.map((h) => (
                        <th key={h} style={{ borderBottom: "1px solid #ddd", textAlign: "left", padding: 6 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rowsValid.map((r, i) => (
                      <tr key={i}>
                        <td style={{ padding: 6 }}>{r.sku ?? ""}</td>
                        <td style={{ padding: 6 }}>{r.nome}</td>
                        <td style={{ padding: 6 }}>{r.preco}</td>
                        <td style={{ padding: 6 }}>{r.estoque}</td>
                        <td style={{ padding: 6 }}>{r.preco_comdesconto ?? ""}</td>
                        <td style={{ padding: 6 }}>{r.status}</td>
                        <td style={{ padding: 6 }}>{r.tipo}</td>
                        <td style={{ padding: 6 }}>{r.id_categoria ?? ""}</td>
                        <td style={{ padding: 6 }}>{r.id_subcategoria ?? ""}</td>
                        <td style={{ padding: 6 }}>{r.peso ?? ""}</td>
                        <td style={{ padding: 6 }}>{r.largura ?? ""}</td>
                        <td style={{ padding: 6 }}>{r.altura ?? ""}</td>
                        <td style={{ padding: 6 }}>{r.comprimento ?? ""}</td>
                        <td style={{ padding: 6 }}>{r.detalhes ?? ""}</td>
                        <td style={{ padding: 6 }}>{(r.imagens || []).join(", ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            )}

            {preview.rowsInvalid.length > 0 && (
              <Alert severity="warning">
                <strong>{t("pasteDialog.invalidRows")}</strong>
                <ul style={{ margin: "6px 0 0 16px" }}>
                  {preview.rowsInvalid.slice(0, 5).map((r, i) => (
                    <li key={i}><code>{r.raw}</code> — {r.reason}</li>
                  ))}
                  {preview.rowsInvalid.length > 5 && <li>…</li>}
                </ul>
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} startIcon={<CloseRoundedIcon />}>{t("pasteDialog.close")}</Button>
        <Button variant="contained" onClick={onApply} disabled={applying || preview.rowsValid.length === 0}>
          {applying ? t("pasteDialog.adding") : t("pasteDialog.applyCreate")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PasteDialog;
